/** Daily Match: one seed per calendar day, shared by everyone. */
import { seedFromString } from '../core/rng';
import type { GradeReport } from '../core/contracts';

export const DAILY_LAUNCH_DATE = '2026-10-05';
/** Daily matches use fixed rules so results are comparable. */
export const DAILY_RULES = { difficulty: 'pro', halfLengthSeconds: 240 } as const;

export interface DailyBest {
  dateKey: string;
  dailyNumber: number;
  seed: number;
  rating: number;
  letter: GradeReport['letter'];
  total: number;
  peakViewers: number;
  goalsCovered: number;
  goals: number;
  attempts: number;
  playedAt: string;
}

const pad = (n: number) => String(n).padStart(2, '0');

/** Local calendar date as YYYY-MM-DD (players expect "today" in their own timezone). */
export function dateKey(d: Date = new Date()): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function keyToUtcDays(key: string): number {
  const [y, m, d] = key.split('-').map(Number) as [number, number, number];
  return Math.floor(Date.UTC(y, m - 1, d) / 86_400_000);
}

/** Daily #1 is the launch date. Dates before launch return 0 or less. */
export function dailyNumber(d: Date | string = new Date()): number {
  const key = typeof d === 'string' ? d : dateKey(d);
  return keyToUtcDays(key) - keyToUtcDays(DAILY_LAUNCH_DATE) + 1;
}

/** Stable seed for the day. Everyone on the same date gets the same match. */
export function dailySeed(d: Date | string = new Date()): number {
  const key = typeof d === 'string' ? d : dateKey(d);
  return seedFromString(key);
}

export function isBetterDaily(candidate: Pick<DailyBest, 'rating' | 'total'>, existing?: Pick<DailyBest, 'rating' | 'total'>): boolean {
  if (!existing) return true;
  if (candidate.rating !== existing.rating) return candidate.rating > existing.rating;
  return candidate.total > existing.total;
}

/** Merge a new result into the per-day bests map (immutable). Attempts always counts up. */
export function recordDaily(
  bests: Readonly<Record<string, DailyBest>>,
  entry: Omit<DailyBest, 'attempts'>,
): { bests: Record<string, DailyBest>; isNewBest: boolean } {
  const prev = bests[entry.dateKey];
  const attempts = (prev?.attempts ?? 0) + 1;
  const isNewBest = isBetterDaily(entry, prev);
  const next = { ...bests, [entry.dateKey]: isNewBest ? { ...entry, attempts } : { ...prev!, attempts } };
  return { bests: next, isNewBest };
}

/** Consecutive days (ending today or yesterday) with a recorded daily result. */
export function dailyStreak(bests: Readonly<Record<string, DailyBest>>, today: Date = new Date()): number {
  let n = 0;
  const d = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  if (!bests[dateKey(d)]) d.setDate(d.getDate() - 1);
  while (bests[dateKey(d)]) { n++; d.setDate(d.getDate() - 1); }
  return n;
}
